import { useQuery } from '@apollo/client';
import { CircularProgress, Icon, IconButton } from '@elipssolution/harfang';
import { mdiAlertCircle, mdiChevronLeft, mdiChevronRight, mdiFileDocumentEdit } from '@mdi/js';
import { Stack, styled, Typography } from '@mui/material';
import clsx from 'clsx';
import { getYear } from 'date-fns';
import { useMemo, useState } from 'react';

import { useSession } from '../../../src/components/session/SessionContext';
import { FETCH_SIGNATURE_INVITATIONS_BY_YEAR, FetchSignatureInvitationsByYearType } from '../api/signer';

const KpiContainer = styled('div')(({ theme: { palette, shape, spacing } }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: spacing(2),
	borderRadius: shape.borderRadius * 1.5,
	border: `1px solid ${palette.divider}`,
	padding: spacing(1, 2, 1, 1),

	'&.error': {
		backgroundColor: `${palette.error.main}1A`,
		borderColor: palette.error.main,
	},
}));

const IconContainer = styled('div')(({ theme: { palette, shape } }) => ({
	height: '40px',
	width: 40,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	borderRadius: shape.borderRadius,
	backgroundColor: palette.primary.main,
	color: palette.getContrastText(palette.primary.main),
}));

const FiscalYearContainer = styled('div')(({ theme: { spacing, palette, shape, shadows } }) => ({
	display: 'flex',
	alignItems: 'center',
	padding: spacing(0.5),
	gap: spacing(1),
	borderRadius: shape.borderRadius,
	boxShadow: shadows[2],
	backgroundColor: palette.primary.main,
	color: palette.getContrastText(palette.primary.main),
}));
const CURRENT_YEAR = getYear(new Date());

const SignatureInvitationsKpis = ({
	remainingSignatureInvitations,
	isRemainingSignatureInvitationsLoading,
}: {
	remainingSignatureInvitations: number;
	isRemainingSignatureInvitationsLoading?: boolean;
}) => {
	const { customerFile: sessionCustomerFile } = useSession();

	const minYear = useMemo(
		() => (sessionCustomerFile ? getYear(new Date(sessionCustomerFile?.startDate)) : CURRENT_YEAR - 5),
		[sessionCustomerFile],
	);

	const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);

	const {
		data,
		error: signatureInvitationsByYearError,
		loading: isSignatureInvitationsLoading,
	} = useQuery<FetchSignatureInvitationsByYearType>(FETCH_SIGNATURE_INVITATIONS_BY_YEAR, {
		variables: {
			year: selectedYear,
		},
		skip: !selectedYear,
	});

	const { sign_signatureInvitationsByYear } = data || {};
	const { signatureInvitations = 0 } = sign_signatureInvitationsByYear || {};

	const navigateToPreviousYear = () => setSelectedYear((prevYear) => prevYear - 1);
	const navigateToNextYear = () => setSelectedYear((prevYear) => prevYear + 1);

	return (
		<>
			<KpiContainer
				className={clsx({
					error: !!signatureInvitationsByYearError,
				})}
			>
				<FiscalYearContainer>
					<IconButton onClick={navigateToPreviousYear} size="small" disabled={selectedYear === minYear}>
						<Icon path={mdiChevronLeft} />
					</IconButton>

					<Typography>{selectedYear}</Typography>

					<IconButton onClick={navigateToNextYear} size="small" disabled={selectedYear === CURRENT_YEAR}>
						<Icon path={mdiChevronRight} />
					</IconButton>
				</FiscalYearContainer>

				{signatureInvitationsByYearError && (
					<Stack paddingLeft={2} paddingRight={2}>
						<Icon path={mdiAlertCircle} color="error" />
					</Stack>
				)}

				{!signatureInvitationsByYearError && (
					<Stack>
						<Typography fontWeight={300} variant="subtitle2">
							Invitation
							{signatureInvitations !== 1 ? 's' : ''} à signer
						</Typography>
						{isSignatureInvitationsLoading ? (
							<CircularProgress />
						) : (
							<Typography fontWeight={800}>
								<Typography fontWeight={800}>{signatureInvitations}</Typography>
							</Typography>
						)}
					</Stack>
				)}
			</KpiContainer>

			<KpiContainer
				className={clsx({
					error: remainingSignatureInvitations < 0,
				})}
			>
				<IconContainer>
					<Icon path={mdiFileDocumentEdit} size="small" />
				</IconContainer>

				<Stack>
					<Typography fontWeight={300} variant="subtitle2">
						Invitation
						{remainingSignatureInvitations > 1 ? 's' : ''} à signer restantes
					</Typography>

					{isRemainingSignatureInvitationsLoading ? (
						<CircularProgress />
					) : (
						<Typography fontWeight={800}>{remainingSignatureInvitations}</Typography>
					)}
				</Stack>
			</KpiContainer>
		</>
	);
};

export default SignatureInvitationsKpis;
